<template>
  <base-drawer :show="show" title="Custom HTML" width="720px" @close="onCloseRequest">
    <drawer-section title="HTML" icon="icon-html5">
      <info-hint v-if="pasteNotices.length > 0" variant="primary" icon="icon-notes" class="mb-3">
        <div>
          <div v-for="notice in pasteNotices" :key="notice">{{ notice }}</div>
        </div>
      </info-hint>

      <textarea
        ref="editor"
        v-model="htmlDraft"
        class="html-editor"
        spellcheck="false"
        placeholder="Paste or write email HTML here. Complete documents are converted to body content automatically."
        @paste="onPaste"
      ></textarea>

      <div class="editor-toolbar">
        <base-button
          size="sm"
          :type="showSmartValues ? 'primary' : 'default'"
          @click="showSmartValues = !showSmartValues"
        >
          <span class="sv-btn-icon">{ }</span>
          Smart variables
        </base-button>
        <span class="editor-size">{{ sizeLabel }}</span>
      </div>

      <div v-if="showSmartValues" class="smart-values-holder">
        <div class="smart-values-header">
          <span class="text-white opacity-7 small">Click a variable to insert it at the cursor</span>
          <button class="panel-close-btn" title="Close" @click="showSmartValues = false">
            <i class="tim-icons icon-simple-remove"></i>
          </button>
        </div>
        <smart-values-panel @insert-value="insertSmartValue" @close="showSmartValues = false" />
      </div>

      <div v-if="stylesDraft" class="hoisted-styles-note">
        <div class="hoisted-styles-header">
          <label class="text-white opacity-7 small mb-0">Style rules from your pasted document</label>
          <base-button type="secondary" size="sm" @click="stylesDraft = ''">Remove</base-button>
        </div>
        <p class="hoisted-styles-hint">Style blocks are moved out of pasted HTML and kept here so they still apply when the email sends. They save with this block.</p>
        <textarea v-model="stylesDraft" class="html-editor html-editor-styles" spellcheck="false"></textarea>
      </div>
    </drawer-section>

    <drawer-section title="Preview" icon="icon-laptop">
      <div class="preview-wrap">
        <!--
          sandbox="allow-same-origin" and nothing else: scripts cannot execute
          because allow-scripts is absent, while same-origin access lets us
          measure the content height. NEVER add allow-scripts.
        -->
        <iframe
          ref="previewFrame"
          class="preview-frame"
          sandbox="allow-same-origin"
          :srcdoc="previewSrcdoc"
          :style="{ height: previewHeight + 'px' }"
          title="Email preview"
          @load="measurePreview"
        ></iframe>
      </div>
    </drawer-section>

    <drawer-section title="Compatibility" icon="icon-alert-circle-exc">
      <info-hint variant="warning" icon="icon-alert-circle-exc" class="mb-3">
        Custom HTML gives you complete control over your email design, but email clients support HTML and CSS differently than web browsers. Always test custom or AI generated HTML before sending a large campaign.
      </info-hint>

      <info-hint v-for="message in unsafeMessages" :key="message" variant="danger" icon="icon-simple-remove" class="mb-2">
        {{ message }}
      </info-hint>

      <div v-if="compatibilityWarnings.length > 0" class="compat-list">
        <div v-for="warning in compatibilityWarnings" :key="warning.message" class="compat-row">
          <i :class="['tim-icons', warning.level === 'warning' ? 'icon-alert-circle-exc warn' : 'icon-bulb-63 info']"></i>
          <span class="text-white">{{ warning.message }}</span>
        </div>
      </div>
      <div v-else-if="unsafeMessages.length === 0" class="compat-clean">No compatibility issues detected</div>
    </drawer-section>

    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" @click="onCloseRequest">Cancel</base-button>
        <base-button type="primary" size="sm" :disabled="unsafeMessages.length > 0" @click="save">Save</base-button>
      </div>
    </template>

    <base-alert-dialog
      :show="showDiscardConfirm"
      title="Discard changes"
      message="Your HTML has unsaved changes. Close without saving?"
      variant="warning"
      show-cancel
      confirm-text="Discard changes"
      confirm-variant="danger"
      cancel-text="Keep editing"
      @confirm="discardAndClose"
      @cancel="showDiscardConfirm = false"
    />
  </base-drawer>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import SmartValuesPanel from '@/components/SmartValuesPanel.vue'
import {
  extractEmailHtmlDocument,
  scanEmailHtmlCompatibility,
  findUnsafeEmailHtml,
} from '@/util/emailHtml'

const props = defineProps<{
  show: boolean
  block: { html?: string; hoistedStyles?: string } | null
}>()

const emit = defineEmits(['close', 'update'])

const editor = ref<HTMLTextAreaElement | null>(null)
const previewFrame = ref<HTMLIFrameElement | null>(null)
const htmlDraft = ref('')
const stylesDraft = ref('')
const pasteNotices = ref<string[]>([])
const showSmartValues = ref(false)
const showDiscardConfirm = ref(false)
const previewHeight = ref(160)

const isDirty = computed(() =>
  htmlDraft.value !== ((props.block && props.block.html) || '') ||
  stylesDraft.value !== ((props.block && props.block.hoistedStyles) || ''))

const unsafeMessages = computed(() => findUnsafeEmailHtml(htmlDraft.value, stylesDraft.value))
const compatibilityWarnings = computed(() => scanEmailHtmlCompatibility(htmlDraft.value, stylesDraft.value))

const sizeLabel = computed(() => {
  const bytes = new Blob([htmlDraft.value]).size
  if (bytes < 1024) return `${bytes} B`
  return `${Math.round(bytes / 1024)} KB`
})

const previewSrcdoc = computed(() =>
  `<!doctype html><html><head><style>body{margin:0;padding:0}</style><style>${stylesDraft.value}</style></head><body>${htmlDraft.value}</body></html>`)

watch(() => props.show, visible => {
  if (visible) {
    htmlDraft.value = (props.block && props.block.html) || ''
    stylesDraft.value = (props.block && props.block.hoistedStyles) || ''
    pasteNotices.value = []
    showSmartValues.value = false
    showDiscardConfirm.value = false
  }
})

watch(previewSrcdoc, () => {
  requestAnimationFrame(() => measurePreview())
})

function measurePreview() {
  const el = previewFrame.value
  if (!el || !el.contentDocument || !el.contentDocument.body) return
  previewHeight.value = Math.min(600, Math.max(160, el.contentDocument.body.scrollHeight))
}

function onPaste() {
  // Let the paste land first, then normalize the full value.
  nextTick(() => {
    const extracted = extractEmailHtmlDocument(htmlDraft.value)
    if (extracted.notices.length === 0) return
    htmlDraft.value = extracted.html
    if (extracted.hoistedStyles) {
      stylesDraft.value = stylesDraft.value
        ? `${stylesDraft.value}\n${extracted.hoistedStyles}`
        : extracted.hoistedStyles
    }
    pasteNotices.value = extracted.notices
  })
}

function insertSmartValue(value: { syntax: string }) {
  const el = editor.value
  const syntax = value && value.syntax ? value.syntax : ''
  if (!el || !syntax) return
  const start = el.selectionStart != null ? el.selectionStart : htmlDraft.value.length
  const end = el.selectionEnd != null ? el.selectionEnd : start
  htmlDraft.value = htmlDraft.value.slice(0, start) + syntax + htmlDraft.value.slice(end)
  nextTick(() => {
    el.focus()
    el.setSelectionRange(start + syntax.length, start + syntax.length)
  })
}

function save() {
  if (unsafeMessages.value.length > 0) return
  emit('update', { html: htmlDraft.value, hoistedStyles: stylesDraft.value })
  emit('close')
}

function onCloseRequest() {
  if (isDirty.value) {
    showDiscardConfirm.value = true
    return
  }
  emit('close')
}

function discardAndClose() {
  showDiscardConfirm.value = false
  emit('close')
}

// BaseDrawer does not handle ESC; each consumer adds its own listener.
function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.show) {
    if (showDiscardConfirm.value) {
      showDiscardConfirm.value = false
      return
    }
    onCloseRequest()
  }
}

onMounted(() => document.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown))
</script>

<style scoped>
.html-editor {
  width: 100%;
  min-height: 340px;
  padding: 12px;
  border: 1px solid rgba(225, 78, 202, 0.4);
  border-radius: 0.4285rem;
  /* Solid: a translucent dark composites to washed gray on the light drawer */
  background: #171826;
  color: #ffffff;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.8125rem;
  line-height: 1.5;
  white-space: pre;
  overflow-x: auto;
  resize: vertical;
}

.html-editor:focus {
  outline: none;
  border-color: #e14eca;
}

.white-content .html-editor {
  background: #fafafc;
  color: #344675;
  border-color: #e3e3e3;
}

.white-content .html-editor::placeholder {
  color: rgba(0, 0, 0, 0.35);
}

.white-content .html-editor:focus {
  border-color: #e14eca;
}

.html-editor-styles {
  min-height: 120px;
}

.editor-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
}

.sv-btn-icon {
  font-weight: 700;
  margin-right: 4px;
}

.editor-size {
  font-size: 0.75rem;
  color: rgba(125, 125, 125, 0.9);
}

.smart-values-holder {
  margin-top: 8px;
  border: 1px solid rgba(125, 125, 125, 0.25);
  border-radius: 0.4285rem;
  max-height: 360px;
  overflow-y: auto;
}

.smart-values-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(125, 125, 125, 0.2);
  position: sticky;
  top: 0;
  z-index: 1;
  background: rgba(30, 30, 47, 0.95);
}

.white-content .smart-values-header {
  background: #f7f7f8;
}

.panel-close-btn {
  border: none;
  background: transparent;
  color: rgba(125, 125, 125, 0.9);
  cursor: pointer;
  padding: 4px;
  line-height: 1;
}

.panel-close-btn:hover {
  color: #e14eca;
}

.hoisted-styles-note {
  margin-top: 12px;
}

.hoisted-styles-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.hoisted-styles-hint {
  font-size: 0.75rem;
  color: rgba(125, 125, 125, 0.9);
  margin: 4px 0 8px;
}

.preview-wrap {
  border: 1px solid rgba(125, 125, 125, 0.25);
  border-radius: 0.4285rem;
  overflow: hidden;
  background: #ffffff;
}

.preview-frame {
  display: block;
  width: 100%;
  border: none;
}

.compat-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.compat-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.8125rem;
}

.compat-row i.warn {
  color: #ff8d72;
}

.compat-row i.info {
  color: #1d8cf8;
}

.compat-clean {
  font-size: 0.8125rem;
  color: rgba(125, 125, 125, 0.9);
}

.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>
